import { Button, Flex, Group, Icon, Skeleton, Stack, Text } from '@inflearn/ds-react';
import { KoOnly, useLang } from '@inflearn/i18n-tools';
import { faGear } from '@inflearn/pro-regular-svg-icons';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { type MouseEvent, type ReactNode, useEffect } from 'react';

import { useDisclosure } from '@mantine/hooks';
import type { MentorDto } from '../../../../resources/@types/uccSchema';
import { INFLEARN_URL } from '../../../constants/urls';
import useCheckFollowing from '../../../hooks/useCheckFollowing';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import useInstructorProfileQuery from '../../../hooks/useInstructorProfileQuery';
import useQueryParams from '../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import useClientAccountProfileQuery from '../../../queries/useClientAccountProfileQuery';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackProfileEvent } from '../../../utils/GTM/useTrackProfileEvent';
import { FollowingButton } from './FollowingButton';
import { HubProfile } from './HubProfile';
import { InstructorInformation } from './InstructorInformation';
import { MentoringInformation } from './MentoringInformation';
import { UserNavigation } from './UserNavigation';
import { UserProfile } from './UserProfile';
import { UserSlugModal } from './UserSlugModal';

export default function InstructorProfileNavigation({
  children,
}: {
  children?: ReactNode;
}) {
  const { id } = useQueryParams();
  const { isMe, isLoading } = useCheckMyProfile();
  const { data: profileMeta } = useUserProfileQuery({ id: Number(id) });
  const { data: myProfile } = useClientAccountProfileQuery({ enabled: isMe });
  const [isSlugModalOpened, slugModalHandler] = useDisclosure(false);

  const { data: instructorProfile } = useInstructorProfileQuery({
    userId: String(id),
    isInstructor: true,
  });
  const { isKo } = useLang();

  useEffect(() => {
    if (myProfile && !myProfile.isSlugConfirmed) {
      slugModalHandler.open();
    }
  }, [myProfile]);

  if (isNil(instructorProfile) || isNil(profileMeta)) {
    return null;
  }

  const isActiveMentor = isKo && Boolean(instructorProfile.mentor?.isActive);

  return (
    <>
      <Flex
        mb={12}
        css={{
          gap: 16,
          flexDirection: 'column',
        }}
      >
        <Stack spacing={12}>
          <Flex
            css={{
              gap: 12,
              justifyContent: 'center',
            }}
          >
            <UserProfile
              slug={profileMeta.slug}
              name={profileMeta.name}
              thumbnailUrl={profileMeta.thumbnailUrl}
            >
              {instructorProfile?.mentor ? (
                <MentoringInformation
                  isActive={isActiveMentor}
                  job={instructorProfile.mentor.job}
                  jobLevel={instructorProfile.mentor.jobLevel}
                  companyName={instructorProfile.mentor.companyName}
                />
              ) : null}
            </UserProfile>
          </Flex>
          <HubProfile userId={profileMeta.id} userName={profileMeta.name} />
        </Stack>
        <InstructorInformation
          course={{
            studentCount: instructorProfile.studentCount,
            reviewCount: instructorProfile.reviewCount,
            reviewAverage: instructorProfile.reviewAverage,
            courseCount: instructorProfile.courseCount,
          }}
          mentor={isActiveMentor ? instructorProfile.mentor : undefined}
        />
        <Skeleton
          visible={isLoading}
          h={isLoading ? 43 : 'auto'}
          css={{
            '&:empty': {
              display: 'none',
            },
          }}
        >
          <ActionButtons
            isMe={isMe}
            userId={profileMeta.id}
            userName={profileMeta.name}
            mentor={isActiveMentor ? instructorProfile.mentor : undefined}
          />
        </Skeleton>
      </Flex>
      <UserNavigation
        userSlug={profileMeta.slug}
        hasBlog={profileMeta.hasBlogs}
        hasPost={profileMeta.hasPosts}
        hasRoadmap={instructorProfile.hasRoadmap}
        hasCourse={!!instructorProfile.courseCount}
        hasReview={!!instructorProfile.reviewCount}
      />
      {children}
      {myProfile && (
        <UserSlugModal
          userId={myProfile.id}
          nickname={myProfile.name}
          thumbnailUrl={myProfile.thumbnail}
          initialSlug={myProfile.slug || ''}
          isOpened={isSlugModalOpened}
          onClose={slugModalHandler.close}
        />
      )}
    </>
  );
}

type ActionButtonsProps = {
  isMe: boolean;
  userId: number;
  userName: string;
  mentor?: MentorDto;
};

function ActionButtons({ isMe, mentor, userId, userName }: ActionButtonsProps) {
  const { trackMixpanelEvent } = useTrackProfileEvent();
  const { t } = useTranslation();
  const { data: status } = useCheckFollowing(String(userId));

  const handleMentoringClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // GTM
    trackMixpanelEvent(eventNames.CLICK_MENTORING_BUTTON);
    window.location.href = e.currentTarget.href;
  };

  return (
    <Group noWrap spacing={10}>
      <KoOnly>
        {isMe && mentor ? (
          <Button
            component={'a'}
            href={INFLEARN_URL.MENTORING_SETTING(mentor.id)}
            aria-label={t('인프런 멘토링 설정 페이지로 이동')}
            variant="outline"
            color="gray"
            size="md"
            radius="md"
            fullWidth
          >
            <Icon icon={faGear} size="md" />
            <Text component="span" ml={6}>
              {t('멘토링 설정')}
            </Text>
          </Button>
        ) : null}
        {!isMe && mentor ? (
          <Button
            component={Link}
            aria-label={t('인프런 멘토링 상세 이동')}
            href={INFLEARN_URL.MENTORING(mentor.id)}
            onClick={handleMentoringClick}
            variant="filled"
            color="infgreen"
            size="md"
            radius="md"
            fullWidth
          >
            {t('멘토링')}
          </Button>
        ) : null}
      </KoOnly>
      {isMe ? null : (
        <FollowingButton
          userId={userId}
          userName={userName}
          isFollowing={Boolean(status?.isFollowing)}
          isInstructor
        />
      )}
    </Group>
  );
}
